import { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { createGlobalStyle } from 'styled-components';
import { bugtracker, useFileUploader, useIdentityContext } from '@99designs/design-services-common';
import { FullPageSpinner } from '@99designs/design-services-layouts';
import { ChatInterface } from '@99designs/feature-chat-brief';
import {
    ClaudeServiceProvider,
    FileUploaderProvider,
    ChatInterface as LegacyChatInterface,
    PartialDesignBrief,
    hydrateChatSession,
    isDynamicFieldsComplete,
    useBriefChatMutation,
    useBriefChatSessionQuery,
} from '@99designs/feature-chat-brief-legacy';
import {
    MultiStepTopBar,
    useBriefQuery,
    useCustomerIdentifiedMutation,
} from '@99designs/feature-design-services-brief';
import { getApolloErrorType } from '@99designs/graph-utils';
import { DS_BRIEF_MULTI_STEP_URL, __url, getCurrentCulture, getSdpUrl } from '@99designs/i18n';
import { sendNavigationEvent, usePage, useSendPageViewOnce } from '@99designs/tracking';
import { ErrorPage, NotFoundErrorPage, PermissionErrorPage } from '../Error';
import type { UploadedFile } from '@99designs/design-services-common';
import type { EsProduct } from '@99designs/feature-chat-brief';
import type { PriceType } from '@99designs/pricing';

const TRACKING_CALLER = 'chat-brief-route-unauthenticated';

/**
 * Visually hides the route's own "Skip to main content" link until it receives
 * keyboard focus. Defined locally (rather than relying on the legacy chat lib's
 * `GlobalChatBriefStyles`) because that global style is only mounted by the legacy
 * `ChatInterface` — it never reaches the DOM on the `?v1=true` path, which left the
 * link unstyled and visibly rendered there. Owning these rules at the shared route
 * shell keeps the skip link correctly hidden-until-focused for both variants.
 */
const SkipLinkStyles = createGlobalStyle`
    .chat-brief-skip-link {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border-width: 0;
    }

    .chat-brief-skip-link:focus,
    .chat-brief-skip-link:focus-visible {
        position: absolute;
        top: 0;
        left: 0;
        width: auto;
        height: auto;
        margin: 0;
        padding: var(--swan-sem-space-2) var(--swan-sem-space-4);
        overflow: visible;
        clip: auto;
        white-space: normal;
        background: var(--swan-sem-color-bg-standard);
        color: var(--swan-sem-color-text-standard);
        z-index: var(--swan-sem-z-index-overlay, 10);
    }
`;

/** The `brief` entity fetched once by the page and handed to the active variant. */
export type ChatBriefData = NonNullable<
    NonNullable<ReturnType<typeof useBriefQuery>['data']>['brief']
>;

function reviewUrl(briefId: string): string {
    return `/experts/request/brief/${briefId}/review`;
}

/** Derives the completion card's expert-service product from the brief. */
function getEsProduct(brief: Pick<ChatBriefData, 'request'>): EsProduct | undefined {
    const name = brief.request?.expertService?.title;
    const expertServicePrice = brief.request?.expertService?.price;
    const price: PriceType | undefined = expertServicePrice
        ? {
              currency: expertServicePrice.currency,
              listPrice: {
                  taxed: expertServicePrice.listPriceCents.taxed,
                  untaxed: expertServicePrice.listPriceCents.untaxed,
              },
          }
        : undefined;

    return name && price ? { name, price } : undefined;
}

interface ChatBriefContentProps {
    brief: ChatBriefData;
    briefId: string;
    onCtaLinkClick?: (url: string, label: string) => void;
    onSwitchToForm?: () => void;
    isSignedIn: boolean;
    claimComplete: boolean;
    correlationId: string | undefined;
    uploadFiles: (files: File[]) => Promise<UploadedFile[]>;
    deleteFile: (uploadId: string) => Promise<void>;
    uploaderLoading: boolean;
    uploaderError: Error | null;
}

/**
 * New (`?v1=true`) chat brief content. This is where the Phase 2 chat frontend is
 * built. Skeleton for now — the shared page shell/data comes from `ChatBrief`.
 * See ESMTCM-1171.
 */
export function ChatBriefV1({
    onCtaLinkClick,
    onSwitchToForm,
    authorizationHeader,
    printProductMpvId,
    brief,
}: Pick<ChatBriefContentProps, 'onCtaLinkClick' | 'onSwitchToForm'> & {
    authorizationHeader: string;
    /**
     * Deliberately `brief.request?.expertService?.printProduct?.mpvId` only, matching what the
     * legacy branch passes below rather than the broader `mpvId` this route computes for the top
     * bar. That one falls back to `brief.product?.fulfilmentMpvId`, which is the right answer for
     * an exit link but the wrong one for a configurator: it would offer the customer options for
     * a product that is not the service's print product.
     */
    printProductMpvId: string;
    brief: Pick<ChatBriefData, 'id' | 'request'>;
}) {
    const briefId = brief.id;
    const baseUrl = process.env.BRIEFS_BRIEF_EXPERIENCE_URL;
    if (!baseUrl) {
        throw new Error('BRIEFS_BRIEF_EXPERIENCE_URL is not configured');
    }

    const handleError = useCallback((error: unknown, context: Record<string, unknown>): void => {
        const notifiableError = error instanceof Error ? error : new Error(String(error));
        bugtracker.notify(notifiableError, (event) => {
            event.addMetadata('chatBrief', context);
        });
    }, []);

    const { uploadFiles, deleteFile, loading, error } = useFileUploader(authorizationHeader);
    const esProduct = getEsProduct(brief);

    return (
        <ChatInterface
            briefId={briefId}
            baseUrl={baseUrl}
            authorizationHeader={authorizationHeader}
            onError={handleError}
            onCtaLinkClick={onCtaLinkClick}
            onReview={() => window.location.assign(reviewUrl(briefId))}
            esProduct={esProduct}
            onSwitchToForm={onSwitchToForm}
            uploader={{ uploadFiles, deleteFile, loading, error }}
            // Undefined rather than an empty mpvId: the print-configuration selector treats an
            // absent product as "cannot offer options" and degrades to the designer-decides
            // answer, whereas a blank id would send a query guaranteed to fail.
            printProduct={
                printProductMpvId ? { mpvId: printProductMpvId, tenant: 'vistaprint' } : undefined
            }
        />
    );
}

/**
 * Legacy chat brief content (the default route). Deprecated — new work goes in
 * the `?v1=true` variant (`ChatBrief`). At cutover, delete this component and the
 * legacy branch/imports below. See ESMTCM-1171.
 */
export function ChatBriefLegacy({
    brief,
    briefId,
    onCtaLinkClick,
    onSwitchToForm,
    isSignedIn,
    claimComplete,
    correlationId,
    uploadFiles,
    deleteFile,
    uploaderLoading,
    uploaderError,
}: ChatBriefContentProps) {
    const [briefChat] = useBriefChatMutation();
    const canLoadSession = isSignedIn ? claimComplete : true;
    const {
        data: chatSessionData,
        loading: chatSessionLoading,
        error: chatSessionError,
    } = useBriefChatSessionQuery({
        variables: { id: briefId },
        errorPolicy: 'all',
        skip: !canLoadSession,
    });

    const { messages: initialMessages, isBriefComplete: isBriefCompleteFromSession } = useMemo(
        () => hydrateChatSession(chatSessionData?.messages.messages ?? []),
        [chatSessionData]
    );

    const [designBrief, setDesignBrief] = useState<PartialDesignBrief>(() => ({
        briefId,
        createdAt: new Date(),
        updatedAt: new Date(),
    }));

    const chatTransport = useCallback(
        async (transportBriefId: string, message: string, expectedMessageCount: number) => {
            const { data, errors } = await briefChat({
                variables: { id: transportBriefId, message, expectedMessageCount },
                errorPolicy: 'all',
            });

            if (!data?.briefChat) {
                const firstError = errors?.[0];
                const serverMessage = firstError?.message;
                const errorType = firstError?.extensions?.type as string | undefined;
                if (errorType === 'INVALID_INPUT' && serverMessage) {
                    throw new Error(`invalid_argument: ${serverMessage}`);
                }
                throw new Error(serverMessage ?? 'No response from chat service');
            }

            return {
                content: data.briefChat.message.content,
                component: data.briefChat.component ?? null,
                document: data.briefChat.document ?? null,
                isBriefComplete: data.briefChat.inferredState?.isBriefComplete === true,
            };
        },
        [briefChat]
    );

    if (chatSessionLoading || !canLoadSession) {
        return <FullPageSpinner />;
    }

    if (chatSessionError && getApolloErrorType(chatSessionError) === 'PERMISSION_DENIED') {
        return <PermissionErrorPage />;
    }

    const esProduct = getEsProduct(brief);

    const initialBriefComplete =
        isBriefCompleteFromSession ||
        isDynamicFieldsComplete(
            brief.dynamicFields as Parameters<typeof isDynamicFieldsComplete>[0]
        );

    return (
        <ClaudeServiceProvider sendMessage={chatTransport}>
            <FileUploaderProvider
                uploader={{
                    uploadFiles,
                    deleteFile,
                    loading: uploaderLoading,
                    error: uploaderError,
                }}
            >
                <LegacyChatInterface
                    designBrief={designBrief}
                    onBriefUpdate={setDesignBrief}
                    initialMessages={initialMessages}
                    initialBriefComplete={initialBriefComplete}
                    initialMaxMessages={chatSessionData?.messages.maxMessages ?? null}
                    onReview={() => window.location.assign(reviewUrl(briefId))}
                    onCtaLinkClick={onCtaLinkClick}
                    onSwitchToForm={onSwitchToForm}
                    printProductMpvId={brief.request?.expertService?.printProduct?.mpvId || ''}
                    requestId={correlationId}
                    serviceProductKey={
                        brief.request?.expertService?.serviceProduct?.productKey ?? undefined
                    }
                    serviceProductVersion={
                        brief.request?.expertService?.serviceProduct?.pimMerchantVersion ??
                        undefined
                    }
                    serviceProductOptions={brief.request?.facts?.serviceProductOptions ?? []}
                    productName={esProduct?.name}
                    productPrice={esProduct?.price}
                />
            </FileUploaderProvider>
        </ClaudeServiceProvider>
    );
}

/**
 * Chat brief route entry. Owns the brief query, authentication / error gating, and
 * the page shell (top bar) — once — then renders the new (`?v1=true`) or legacy
 * chat content. See ESMTCM-1171.
 */
export function ChatBrief() {
    const { jobId } = useParams();
    const briefId = jobId as string;
    const [searchParams] = useSearchParams();
    const useV1 = searchParams.get('v1') === 'true';
    const locale = useMemo(() => getCurrentCulture(), []);
    const { SignIn } = useIdentityContext();
    const navigate = useNavigate();
    const location = useLocation();
    const { pageData } = usePage();
    const { isSignedIn, authorizationHeader } = useIdentityContext();
    const {
        uploadFiles,
        deleteFile,
        loading: uploaderLoading,
        error: uploaderError,
    } = useFileUploader(authorizationHeader);
    const [customerIdentified] = useCustomerIdentifiedMutation();

    useSendPageViewOnce(locale);

    const { data, loading, error } = useBriefQuery({
        variables: { id: briefId },
        errorPolicy: 'all',
        fetchPolicy: 'cache-first',
    });

    const correlationId = data?.brief?.correlationId;
    const isClaimed = data?.brief?.request?.isClaimed;
    const [claimComplete, setClaimComplete] = useState(false);

    useEffect(() => {
        if (!isSignedIn || !correlationId) return;
        if (isClaimed === false) {
            customerIdentified({ variables: { requestId: correlationId } }).then(() => {
                setClaimComplete(true);
            });
        } else {
            setClaimComplete(true);
        }
    }, [isSignedIn, isClaimed, correlationId, customerIdentified]);

    if (loading) {
        return <FullPageSpinner />;
    }

    if (error) {
        const errorType = getApolloErrorType(error);
        if (errorType === 'UNAUTHENTICATED') {
            SignIn(undefined, { trackingCaller: TRACKING_CALLER });
            return <FullPageSpinner />;
        }
        if (errorType === 'PERMISSION_DENIED') {
            return <PermissionErrorPage />;
        }
        if (errorType === 'NOT_FOUND') {
            return <NotFoundErrorPage />;
        }
        return <ErrorPage error={error} metadata={{ briefId }} />;
    }

    if (!data) {
        return <FullPageSpinner />;
    }

    const brief = data.brief;

    const handleCtaLinkClick = (url: string, label: string) => {
        sendNavigationEvent({
            ...pageData,
            category: pageData.pageSection,
            label: `Content: ${label}`,
            navigationDetail: 'CtaLinkClick',
            eventDetail: `${window.location.pathname};${url};${label};CtaLinkButton`,
        });
    };

    const handleSwitchToForm = () => {
        sendNavigationEvent({
            ...pageData,
            category: pageData.pageSection,
            label: 'Content: Fill Out Form Instead',
            navigationDetail: 'FillOutFormInstead',
            eventDetail: `${window.location.pathname};/experts/request/brief/create;Fill Out Form Instead;FillOutFormInsteadButton`,
        });
        const params = new URLSearchParams(location.state?.createSearchParams ?? '');
        params.set('chatBrief', 'false');
        const path = __url(`${DS_BRIEF_MULTI_STEP_URL}/create`);
        navigate(`${path}?${params.toString()}`);
    };
    const mpvId =
        brief.request?.expertService?.printProduct?.mpvId || brief.product?.fulfilmentMpvId;
    const productName = brief.request?.expertService?.title;
    const topBarExitUrl = mpvId ? getSdpUrl(mpvId) : __url('/design');

    return (
        <>
            <Helmet>
                <title>Design Services Chat Brief | Vistaprint</title>
            </Helmet>
            <SkipLinkStyles />
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100dvh',
                    overflow: 'hidden',
                    position: 'relative',
                    backgroundColor: 'var(--swan-sem-color-bg-strong)',
                }}
            >
                <a
                    href="#chat-main-content"
                    className="chat-brief-skip-link"
                    onClick={(e) => {
                        e.preventDefault();
                        const main = document.getElementById('chat-main-content');
                        if (main) {
                            main.focus();
                            window.history.replaceState(null, '', '#chat-main-content');
                        }
                    }}
                >
                    Skip to main content
                </a>
                <header>
                    <MultiStepTopBar
                        title={productName || 'Custom Design'}
                        exitUrl={topBarExitUrl}
                    />
                </header>
                <main
                    id="chat-main-content"
                    tabIndex={-1}
                    style={{ flex: 1, minHeight: 0, outline: 'none' }}
                >
                    {useV1 && isSignedIn && !claimComplete ? (
                        <FullPageSpinner />
                    ) : useV1 ? (
                        <ChatBriefV1
                            authorizationHeader={authorizationHeader}
                            onCtaLinkClick={handleCtaLinkClick}
                            onSwitchToForm={
                                location.state?.createSearchParams ? handleSwitchToForm : undefined
                            }
                            printProductMpvId={
                                brief.request?.expertService?.printProduct?.mpvId || ''
                            }
                            brief={brief}
                        />
                    ) : (
                        <ChatBriefLegacy
                            brief={brief}
                            briefId={briefId}
                            onCtaLinkClick={handleCtaLinkClick}
                            onSwitchToForm={
                                location.state?.createSearchParams ? handleSwitchToForm : undefined
                            }
                            isSignedIn={isSignedIn}
                            claimComplete={claimComplete}
                            correlationId={correlationId}
                            uploadFiles={uploadFiles}
                            deleteFile={deleteFile}
                            uploaderLoading={uploaderLoading}
                            uploaderError={uploaderError}
                        />
                    )}
                </main>
            </div>
        </>
    );
}
